import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'particles/themes/styled'
import { OptionsState } from 'particles/Dropdown'
import { GridFilterItem, GridApi, GridSortDirection } from '@mui/x-data-grid-pro'
import Divider from 'components/Shared/Divider'
import { SortItem } from './PartiTable'
import _ from 'lodash'
import ColumnFilterTooltip from './FilterAndSort/ColumnFilterTooltip'
import ColumnSortTooltip from './FilterAndSort/ColumnSortTooltip'
import Chip from 'particles/Chip'
import { FIELDS_TYPE, getSubFieldValues } from 'components/utils/filterAndSort'
import { useNewTranslation } from 'hooks'
import { TableViewType } from '.'
import { useFilterModel, useFilterOptions, useSortItem } from './hooks'

export interface FilterItem extends GridFilterItem {
    label: string
}

interface SortState {
    sort: GridSortDirection
    sortDirectionLabel: string
}

interface FilterBarProps {
    className?: string
    onShowColumnFilter: (e: React.MouseEvent<HTMLDivElement>, value: string, isSort?: boolean) => void | undefined
    onRemoveFilter: (value: string, isSort?: boolean) => void | undefined
    selectedFilterAndSort: { field: string; isSort?: boolean; hideTooltip?: boolean }
    apiRef: React.MutableRefObject<GridApi>
    onSelectFilter: (options: OptionsState[], sortOrColumn: FIELDS_TYPE) => void
    onSelectSort: (item: SortItem) => void
    onCloseFilterField: () => void
    viewType?: TableViewType
}

const FilterAndSortBar: React.FC<FilterBarProps> = ({
    className,
    onShowColumnFilter,
    onRemoveFilter,
    selectedFilterAndSort,
    apiRef,
    onSelectFilter,
    onSelectSort,
    onCloseFilterField,
    viewType,
}) => {
    const [t] = useNewTranslation()
    const sortRef = useRef<HTMLDivElement | null>(null)
    const [filterEl, setFilterEl] = useState<HTMLDivElement | null>(null)
    const [sortEl, setSortEl] = useState<HTMLDivElement | null>(null)

    const subFieldValues = getSubFieldValues(t)

    const filters = (useFilterModel(viewType)?.items || []) as FilterItem[]
    const filterOptions = useFilterOptions(viewType) as {
        value: string
        label: string
    }[]
    const sortItem = useSortItem(viewType) as {
        field: string
        sort: GridSortDirection
        fieldLabel: string
        sortDirectionLabel: string
    }

    const { field: sortField, fieldLabel: sortFieldLabel, sortDirectionLabel } = sortItem || {}

    const currentColumn = Object.keys(subFieldValues).includes(selectedFilterAndSort.field)
        ? subFieldValues[selectedFilterAndSort.field]
        : apiRef?.current?.getColumn && apiRef?.current?.getColumn(selectedFilterAndSort.field)

    useEffect(() => {
        const filterChip = document.getElementById(`${selectedFilterAndSort.field}-filter`)
        if (selectedFilterAndSort.isSort && !selectedFilterAndSort.hideTooltip) {
            setSortEl(sortRef.current)
        } else if (!selectedFilterAndSort.isSort) {
            filterChip?.click()
        }
    }, [selectedFilterAndSort.field, sortField, selectedFilterAndSort.hideTooltip])

    const getFilterLabels = useCallback(
        field => {
            const fieldFilters = filters.filter(item => item.columnField === field)
            const filterLabels = fieldFilters.map(item => item.label)
            const formatLabels = filterLabels.join(', ')
            return formatLabels && `: ${formatLabels}`
        },
        [filters],
    )

    const handleSort = (option: OptionsState, sortOrColumn?: string | SortState) => {
        if (typeof sortOrColumn === 'object' && option.value) {
            onSelectSort({
                field: option.value,
                fieldLabel: option.label,
                sort: sortOrColumn.sort,
                sortDirectionLabel: sortOrColumn.sortDirectionLabel,
            })
        }
    }

    return (
        <>
            <div className={className}>
                {sortItem && !_.isEmpty(sortItem) && (
                    <Chip
                        ref={sortRef}
                        label={`${sortFieldLabel}: ${sortDirectionLabel}`}
                        onClick={e => {
                            onShowColumnFilter(e, sortItem.field, true)
                            setSortEl(e.currentTarget)
                        }}
                        onDelete={() => onRemoveFilter(sortItem.field, true)}
                    />
                )}
                {!_.isEmpty(sortItem) && !_.isEmpty(filterOptions) && (
                    <Divider width="1px" height="36px" className="divider" />
                )}
                {filterOptions &&
                    filterOptions.map(({ value, label }) => (
                        <Chip
                            id={`${value}-filter`}
                            key={value}
                            label={`${label}${getFilterLabels(value)}`}
                            onClick={e => {
                                onShowColumnFilter(e, value as string)
                                setFilterEl(e.currentTarget)
                            }}
                            onDelete={() => onRemoveFilter(value as string)}
                        />
                    ))}
            </div>
            {sortEl && !selectedFilterAndSort.hideTooltip ? (
                <ColumnSortTooltip
                    anchorEl={sortEl}
                    onClose={(): void => {
                        setSortEl(null)
                        onCloseFilterField()
                    }}
                    onChange={handleSort}
                    column={currentColumn}
                    sortItem={sortItem}
                />
            ) : (
                <ColumnFilterTooltip
                    anchorEl={filterEl}
                    onClose={(): void => {
                        setFilterEl(null)
                        onCloseFilterField()
                    }}
                    onChange={onSelectFilter}
                    column={currentColumn}
                    viewType={viewType}
                />
            )}
        </>
    )
}

export default styled(FilterAndSortBar)`
    display: flex;
    align-items: center;
    padding: 4px 12px;
    gap: 8px;
    overflow-x: auto;
    flex-wrap: nowrap;
    min-height: 36px;
    .divider {
        flex: none; // Prevent divider from shrinking
    }
`
